import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import BulkReviewPanel from "../components/BulkReviewPanel";
import BulkSendProgressPanel from "../components/ChatAutomations/BulkSendProgressPanel";
import MemberEventsPanel from "../components/MemberEventsPanel";
import MemberMessagePanel from "../components/MemberMessagePanel";
import NewMemberDetailPanel from "../components/NewMemberDetailPanel";
import NewMemberInsights from "../components/NewMemberInsights";
import PageHeader from "../components/PageHeader";
import ResponsiveTable from "../components/ResponsiveTable";
import { SEND_PERSONAL_MESSAGES } from "../gql/queries/cancellations";
import { ADMIN_NEW_MEMBERS } from "../gql/queries/newMembers";
import { foldText } from "../helpers/foldText";
import {
  cleanTitle,
  FIRST_EVENT_LABELS,
  type FirstEventKind,
  firstEventKinds,
  hasLeft,
  type NewMember,
  type NewMembersResult,
  PASS_LABELS,
  type PassKind,
  passKind,
  signedUpText,
  statusColour,
} from "../helpers/newMembers";
import { messageToolLabel, useRecentMessages } from "../helpers/recentMessages";
import { Spacing } from "../types/enum";

const DAY_CHOICES = [7, 14, 30];
const DEFAULT_DAYS = 14;

type Panel = "message" | "detail" | "events";

/** Every distinct answer present, for a filter — only ones that would return rows. */
const answers = (members: NewMember[], label: (member: NewMember) => string | null | undefined) =>
  [...new Set(members.map(label).filter(Boolean))].sort() as string[];

/**
 * New Members: everyone who came through the platform's new signup flow.
 *
 * What she told us on the way in, what she has done since, the event the
 * invitation rules would send her, and a follow-up written from her answers —
 * which she can be sent from here, in a batch, with anyone messaged recently by
 * another tool unticked.
 */
const NewMembersPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [search, setSearch] = useState("");
  const [redFlagsOnly, setRedFlagsOnly] = useState(false);
  const [priority, setPriority] = useState("");
  const [challenge, setChallenge] = useState("");
  const [firstEvent, setFirstEvent] = useState<FirstEventKind | "">("");
  const [pass, setPass] = useState<PassKind | "">("");
  /** Hold back today's (or this week's) signups, unless they're a red flag. */
  const [minAge, setMinAge] = useState(0);
  /** Members who've already left are hidden until asked for. */
  const [showLeft, setShowLeft] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [angleFor, setAngleFor] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<{ email: string; panel: Panel } | null>(null);
  /** Bulk send is a two-step: review every message, then send. */
  const [reviewing, setReviewing] = useState(false);

  const { skipDays, setSkipDays, loadFor, recentByUser, contactedWithinSkip } =
    useRecentMessages(7);

  const [load, { data, loading, error }] = useLazyQuery(ADMIN_NEW_MEMBERS, {
    fetchPolicy: "network-only",
    onCompleted: (res) => {
      const result: NewMembersResult | undefined = res?.adminNewMembers;
      setAngleFor({});
      loadFor((result?.members ?? []).map((member) => member.userId));
    },
  });
  const [runSend, { loading: sending }] = useMutation(SEND_PERSONAL_MESSAGES);
  const [sendId, setSendId] = useState<string | null>(null);

  useEffect(() => {
    void load({ variables: { days } });
  }, [load, days]);

  const result: NewMembersResult | undefined = data?.adminNewMembers;
  const allMembers = useMemo(() => result?.members ?? [], [result]);

  const messageOf = (member: NewMember): string => {
    const key = angleFor[member.email] ?? member.defaultAngle;
    return member.angles.find((angle) => angle.key === key)?.message ?? member.message;
  };
  const recentOf = (member: NewMember) => recentByUser.get(member.userId);

  const rows = useMemo(() => {
    const term = foldText(search.trim());
    return allMembers.filter((member) => {
      if (!showLeft && hasLeft(member)) {
        return false;
      }
      if (redFlagsOnly && !member.redFlag) {
        return false;
      }
      // A red flag is shown however new she is: that is the point of one.
      if (minAge > 0 && !member.redFlag && member.ageDays < minAge) {
        return false;
      }
      if (priority && member.priorityLabel !== priority) {
        return false;
      }
      if (challenge && member.challengeLabel !== challenge) {
        return false;
      }
      if (firstEvent && !firstEventKinds(member).includes(firstEvent)) {
        return false;
      }
      if (pass && passKind(member) !== pass) {
        return false;
      }
      if (!term) {
        return true;
      }
      return foldText(
        [member.name, member.email, member.timezone ?? "", member.selectedTimezone ?? ""].join(" "),
      ).includes(term);
    });
  }, [allMembers, search, showLeft, redFlagsOnly, minAge, priority, challenge, firstEvent, pass]);

  // Pre-tick everyone on screen who hasn't been messaged inside the skip window.
  // Inlined rather than reusing contactedWithinSkip, which is rebuilt every render.
  useEffect(() => {
    setSelected(
      new Set(
        rows
          .filter((member) => {
            const recent = recentByUser.get(member.userId);
            return !(recent !== undefined && recent.days <= skipDays);
          })
          .map((member) => member.email),
      ),
    );
  }, [rows, recentByUser, skipDays]);

  const leftCount = allMembers.filter(hasLeft).length;
  const skippedCount = rows.filter((member) => contactedWithinSkip(member.userId)).length;
  const redFlagCount = rows.filter((member) => member.redFlag).length;

  const toggle = (email: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });

  const toggleAllVisible = (on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      for (const member of rows) {
        if (on) {
          next.add(member.email);
        } else {
          next.delete(member.email);
        }
      }
      return next;
    });

  // Only what's on screen can be sent to — a ticked row that later got hidden
  // must drop out of the send, not travel along with it invisibly.
  const recipients = rows
    .filter((member) => selected.has(member.email))
    .map((member) => ({ member, message: messageOf(member) }));

  const onSend = async () => {
    if (recipients.length === 0) {
      enqueueSnackbar("Select at least one member.", { variant: "info" });
      return;
    }
    try {
      const { data: res } = await runSend({
        variables: {
          input: {
            recipients: recipients.map((r) => ({ email: r.member.email, message: r.message })),
            campaign: "new_member",
            source: "NEW_MEMBERS",
          },
        },
      });
      const sent = res?.chatAutomationsSendPersonalMessages;
      if (sent?.sendId) {
        setSendId(sent.sendId);
        setReviewing(false);
        enqueueSnackbar(
          `Queued for ${sent.total} member(s).` +
            (sent.skipped?.length
              ? ` ${sent.skipped.length} skipped — messaged too recently.`
              : ""),
          { variant: "info" },
        );
        return;
      }
      enqueueSnackbar(
        `Sent to ${sent?.succeeded ?? 0} member${sent?.succeeded === 1 ? "" : "s"}.` +
          (sent?.failed ? ` ${sent.failed} failed.` : "") +
          (sent?.notFound?.length ? ` ${sent.notFound.length} matched no member.` : ""),
        { variant: sent?.failed ? "warning" : "success" },
      );
      setReviewing(false);
      loadFor(allMembers.map((member) => member.userId));
    } catch (caught) {
      enqueueSnackbar(caught instanceof Error ? caught.message : "Send failed.", {
        variant: "error",
      });
    }
  };

  const openMember = open ? rows.find((member) => member.email === open.email) : undefined;
  const togglePanel = (member: NewMember, panel: Panel) => {
    setReviewing(false);
    setOpen(
      open?.email === member.email && open.panel === panel ? null : { email: member.email, panel },
    );
  };
  const allVisibleOn = rows.length > 0 && rows.every((member) => selected.has(member.email));

  return (
    <>
      <PageHeader title="New Members">
        Everyone who came through the new signup flow: what she told us, what she has done since,
        the event the invitation rules would send her, and a follow-up written from her answers.
        Press 💬 to read and send her message, 📨 to see her answers and share or invite her, or 🔎
        for any other events she would enjoy.
      </PageHeader>
      {sendId && (
        <BulkSendProgressPanel
          sendId={sendId}
          onComplete={() => loadFor(allMembers.map((member) => member.userId))}
        />
      )}

      <Card sx={{ minWidth: 0 }}>
        <Box p={Spacing.l} sx={{ minWidth: 0 }}>
          <Stack spacing={Spacing.m} sx={{ minWidth: 0 }}>
            <Box display="flex" alignItems="center" gap={Spacing.m} flexWrap="wrap">
              <TextField
                size="small"
                placeholder="🔎 Search name, email or time zone…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ minWidth: 220, flexGrow: 1 }}
              />
              <TextField
                select
                size="small"
                label="Signed up in the last"
                value={days}
                onChange={(event) => setDays(Number(event.target.value))}
                sx={{ minWidth: 150 }}
              >
                {DAY_CHOICES.map((choice) => (
                  <MenuItem key={choice} value={choice}>
                    {choice} days
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Joined at least"
                value={minAge}
                onChange={(event) => setMinAge(Number(event.target.value))}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value={0}>any time</MenuItem>
                <MenuItem value={1}>1 day ago</MenuItem>
                <MenuItem value={2}>2 days ago</MenuItem>
                <MenuItem value={3}>3 days ago</MenuItem>
                <MenuItem value={7}>7 days ago</MenuItem>
              </TextField>
              <TextField
                label="Skip if messaged within"
                type="number"
                size="small"
                value={skipDays}
                onChange={(event) => setSkipDays(Math.max(0, Number(event.target.value) || 0))}
                slotProps={{ htmlInput: { min: 0, style: { width: 64 } } }}
                helperText="days"
              />
              <Button
                variant="outlined"
                onClick={() => void load({ variables: { days } })}
                disabled={loading}
              >
                {loading ? "Loading…" : "↻ Refresh"}
              </Button>
            </Box>

            <Box display="flex" alignItems="center" gap={Spacing.m} flexWrap="wrap">
              <TextField
                select
                size="small"
                label="Wants English"
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
                sx={{ minWidth: 190 }}
              >
                <MenuItem value="">Any answer</MenuItem>
                {answers(allMembers, (member) => member.priorityLabel).map((label) => (
                  <MenuItem key={label} value={label}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="What makes it hard"
                value={challenge}
                onChange={(event) => setChallenge(event.target.value)}
                sx={{ minWidth: 190 }}
              >
                <MenuItem value="">Any answer</MenuItem>
                {answers(allMembers, (member) => member.challengeLabel).map((label) => (
                  <MenuItem key={label} value={label}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="First event"
                value={firstEvent}
                onChange={(event) => setFirstEvent(event.target.value as FirstEventKind | "")}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">Any</MenuItem>
                {(Object.keys(FIRST_EVENT_LABELS) as FirstEventKind[]).map((kind) => (
                  <MenuItem key={kind} value={kind}>
                    {FIRST_EVENT_LABELS[kind]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Her pass"
                value={pass}
                onChange={(event) => setPass(event.target.value as PassKind | "")}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="">Any</MenuItem>
                {(Object.keys(PASS_LABELS) as PassKind[]).map((kind) => (
                  <MenuItem key={kind} value={kind}>
                    {PASS_LABELS[kind]}
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={
                  <Switch
                    checked={redFlagsOnly}
                    onChange={(event) => setRedFlagsOnly(event.target.checked)}
                  />
                }
                label={<Typography variant="body2">Red flags only</Typography>}
              />
            </Box>

            {error && <Alert severity="error">Couldn't load new members: {error.message}</Alert>}
            {result && result.total === 0 && (
              <Alert severity="info">
                Nobody has come through the new signup flow in the last {result.days} days.
              </Alert>
            )}

            {result && result.total > 0 && (
              <>
                {redFlagCount > 0 && (
                  <Alert severity="error">
                    {redFlagCount} red flag{redFlagCount === 1 ? "" : "s"}: skipped booking a first
                    event, or missed the one she booked, and has been to nothing since. They're at
                    the top — reach them today.
                  </Alert>
                )}
                {skippedCount > 0 && (
                  <Alert severity="warning">
                    {skippedCount} member{skippedCount === 1 ? " was" : "s were"} already sent a
                    chat message in the last {skipDays} days and {skippedCount === 1 ? "is" : "are"}{" "}
                    unselected. Re-tick anyone you still want to reach.
                  </Alert>
                )}

                <Box display="flex" alignItems="center" gap={Spacing.m} flexWrap="wrap">
                  <Typography variant="body2" color="textSecondary">
                    Showing {rows.length} of {allMembers.length} · {recipients.length} selected
                    {!showLeft && leftCount > 0 ? ` · ${leftCount} who left hidden` : ""}
                  </Typography>
                  {/* Opens the review rather than sending — see BulkReviewPanel. */}
                  <Button
                    variant="contained"
                    onClick={() => {
                      setOpen(null);
                      setReviewing(true);
                    }}
                    disabled={sending || recipients.length === 0}
                  >
                    Review {recipients.length} message{recipients.length === 1 ? "" : "s"}
                  </Button>
                  <Button variant="text" onClick={() => setShowInsights((value) => !value)}>
                    {showInsights ? "Hide insights" : "📊 Insights"}
                  </Button>
                  {leftCount > 0 && (
                    <Button variant="text" onClick={() => setShowLeft((value) => !value)}>
                      {showLeft ? `Hide ${leftCount} who left` : `Show ${leftCount} who left`}
                    </Button>
                  )}
                </Box>

                {/* Reads the filtered rows, so narrowing the table narrows this. */}
                {showInsights && (
                  <NewMemberInsights
                    members={rows}
                    messaged={(member) => recentOf(member) !== undefined}
                  />
                )}

                <ResponsiveTable minWidth={1360} maxHeight="min(560px, 60vh)">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allVisibleOn}
                          indeterminate={
                            !allVisibleOn && rows.some((member) => selected.has(member.email))
                          }
                          onChange={(event) => toggleAllVisible(event.target.checked)}
                        />
                      </TableCell>
                      <TableCell>Member</TableCell>
                      <TableCell>Signed up</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Wants English</TableCell>
                      <TableCell>What makes it hard</TableCell>
                      <TableCell>First event</TableCell>
                      <TableCell>Since</TableCell>
                      <TableCell>Her message suggests</TableCell>
                      <TableCell>Angle</TableCell>
                      <TableCell>Outreach</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((member) => {
                      const recent = recentOf(member);
                      const skip = contactedWithinSkip(member.userId);
                      const kinds = firstEventKinds(member);
                      const { event, invitation } = member.suggestion;
                      return (
                        <TableRow
                          key={member.email}
                          hover
                          sx={skip ? { opacity: 0.55 } : undefined}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selected.has(member.email)}
                              onChange={() => toggle(member.email)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {member.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {member.email}
                            </Typography>
                            {member.redFlag && (
                              <Chip
                                size="small"
                                color="error"
                                label="red flag"
                                title={member.redFlagLabel ?? ""}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {signedUpText(member.ageDays)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={member.statusLabel}
                              color={statusColour(member.status)}
                            />
                            {member.status === "guest" && member.guestTokensLeft != null && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                {member.guestTokensLeft} of 3 left
                                {member.guestDaysLeft != null ? ` · ${member.guestDaysLeft}d` : ""}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{member.priorityLabel ?? "—"}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {member.challengeLabel ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {member.firstEventSkipped ? (
                              <Chip
                                size="small"
                                color="error"
                                variant="outlined"
                                label="skipped booking"
                              />
                            ) : member.firstEventId ? (
                              <>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  title={member.firstEventTitle ?? ""}
                                  sx={{
                                    maxWidth: 200,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {cleanTitle(member.firstEventTitle) || "(no longer listed)"}
                                </Typography>
                                {kinds[1] && (
                                  <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    color={
                                      kinds[1] === "came"
                                        ? "success.main"
                                        : kinds[1] === "missed"
                                          ? "error.main"
                                          : "text.secondary"
                                    }
                                  >
                                    {FIRST_EVENT_LABELS[kinds[1]].toLowerCase()}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">
                              {member.eventsAttended} event{member.eventsAttended === 1 ? "" : "s"}
                            </Typography>
                            {member.acceptedUpcoming > 0 && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                {member.acceptedUpcoming} booked ahead
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {invitation ? (
                              <>
                                <Typography variant="caption" fontWeight={600} display="block">
                                  Her Invitations
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  holds {cleanTitle(invitation.title)}
                                </Typography>
                              </>
                            ) : event ? (
                              <>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  title={event.why}
                                  sx={{
                                    maxWidth: 220,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {cleanTitle(event.title)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  {event.typeLabel}
                                  {event.when ? ` · ${event.when}` : ""}
                                </Typography>
                                {event.afterPass && (
                                  <Chip size="small" color="warning" label="after her pass" />
                                )}
                              </>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                nothing she can open
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {member.angles.length > 1 ? (
                              <Select
                                size="small"
                                value={angleFor[member.email] ?? member.defaultAngle}
                                onChange={(change) =>
                                  setAngleFor((current) => ({
                                    ...current,
                                    [member.email]: change.target.value,
                                  }))
                                }
                                sx={{ fontSize: ".8rem", minWidth: 140 }}
                              >
                                {member.angles.map((angle) => (
                                  <MenuItem key={angle.key} value={angle.key}>
                                    {angle.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                {member.angles[0]?.label.replace("Her signup answers: ", "")}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {recent === undefined ? (
                              <Typography variant="caption" color="textSecondary">
                                never
                              </Typography>
                            ) : (
                              <Typography
                                variant="caption"
                                color={skip ? "error" : "textSecondary"}
                                fontWeight={skip ? 600 : 400}
                              >
                                💬 {recent.days === 0 ? "today" : `${recent.days}d`}
                                {messageToolLabel(recent.campaign)
                                  ? ` · ${messageToolLabel(recent.campaign)}`
                                  : ""}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              {(["message", "detail", "events"] as const).map((panel) => (
                                <Button
                                  key={panel}
                                  size="small"
                                  variant={
                                    open?.email === member.email && open.panel === panel
                                      ? "contained"
                                      : "outlined"
                                  }
                                  onClick={() => togglePanel(member, panel)}
                                >
                                  {panel === "message" ? "💬" : panel === "detail" ? "📨" : "🔎"}
                                </Button>
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </ResponsiveTable>

                {reviewing && (
                  <BulkReviewPanel
                    recipients={recipients.map(({ member, message }) => ({
                      email: member.email,
                      name: member.name,
                      message,
                      warning: contactedWithinSkip(member.userId)
                        ? `messaged ${recentOf(member)?.days}d ago`
                        : undefined,
                    }))}
                    sending={sending}
                    onSend={onSend}
                    onClose={() => setReviewing(false)}
                  />
                )}

                {openMember && open?.panel === "message" && (
                  <MemberMessagePanel
                    email={openMember.email}
                    name={openMember.name}
                    initialMessage={messageOf(openMember)}
                    campaign="new_member"
                    warning={
                      contactedWithinSkip(openMember.userId)
                        ? `She was messaged ${recentOf(openMember)?.days}d ago, inside your ${skipDays}-day skip window.`
                        : undefined
                    }
                    onClose={() => setOpen(null)}
                    onSent={() => loadFor(allMembers.map((member) => member.userId))}
                  />
                )}

                {openMember && open?.panel === "detail" && (
                  <NewMemberDetailPanel member={openMember} onClose={() => setOpen(null)} />
                )}

                {openMember && open?.panel === "events" && (
                  <MemberEventsPanel
                    email={openMember.email}
                    name={openMember.name}
                    onClose={() => setOpen(null)}
                  />
                )}
              </>
            )}
          </Stack>
        </Box>
      </Card>
    </>
  );
};

export default NewMembersPage;
